// src/components/cash_balance/cash_balance.js

import React, { useEffect, useState, useCallback } from 'react';
import axiosWithAuth from '../../helpers/AxiosHelper';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import LoadingState from '../common/LoadingState';
import PageHeader from '../common/PageHeader';
import './cash_balance.css';
import errorMessage, { describeError } from '../../helpers/errorMessage';

const euroFormatter = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });

// GET /cash-balance returns { workspaces, rows } - one tab per workspace (configured ones first,
// then any compartment that has data but no workspace yet), and the newest balance per account.
// See backend/api/cash_balance.js for how a balance is resolved to its account and workspace.

// --- FORMAT DATE ---
// Balance dates arrive as 'YYYY-MM-DD'; parsed as a local date so the day never shifts.
const formatDate = (value) => {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('default', { year: 'numeric', month: 'short', day: 'numeric' });
};

const toQueryDate = (dateObj) => {
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const sumBalances = (rows) => rows.reduce((sum, row) => sum + (Number(row.balance) || 0), 0);

// --- EXPORT TO EXCEL ---
// Full balance history for one workspace. No total row: summing the same account over many dates
// is not a cash balance.
const exportCashBalanceToExcel = (rows, fileName) => {
  if (!rows || !rows.length) return;

  const exportData = rows.map((row) => ({
    Compartment: row.compartment || '',
    Account: row.account,
    'Cash Balance': row.balance,
    'Balance Date': row.balance_date,
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Cash Balance');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), fileName);
};

// --- MAIN COMPONENT ---
const CashBalanceDashboard = ({ setLoading = () => {} }) => {
  const [data, setData] = useState(null);
  const [filterDate, setFilterDate] = useState(null);
  const [error, setError] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState(null);

  // --- FETCH DATA ---
  const fetchData = useCallback(
    async (dateObj = filterDate) => {
      setLoading(true);
      try {
        const res = await axiosWithAuth.get('/cash-balance', { params: dateObj ? { date: toQueryDate(dateObj) } : {} });
        setData(res.data || { workspaces: [], rows: [] });
        setError(null);
      } catch (err) {
        console.error('[fetchData] error:', errorMessage(err));
        setError(errorMessage(err, 'Failed to fetch cash balance data.'));
      } finally {
        setLoading(false);
      }
    },
    [filterDate, setLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- DOWNLOAD ALL DATA ---
  const downloadAllData = async (workspace) => {
    const fileName = `cash_balance_${workspace.key}.xlsx`;
    try {
      setLoading(true);
      const params = { mode: 'all', ...(filterDate ? { date: toQueryDate(filterDate) } : {}) };
      setDownloadMessage(null);
      const res = await axiosWithAuth.get('/cash-balance', { params });
      const rows = (res.data?.rows || []).filter((row) => row.workspace === workspace.key);
      if (!rows.length) {
        setDownloadMessage('There is no cash balance data to download for this date.');
        return;
      }
      exportCashBalanceToExcel(rows, fileName);
    } catch (err) {
      setDownloadMessage(await describeError(err, `Cash Balance: Download Excel (${fileName})`));
    } finally {
      setLoading(false);
    }
  };

  // --- RENDER ONE ACCOUNTS TABLE ---
  const renderAccountsTable = (rows, totalLabel) => (
    <DataTable
      value={rows}
      dataKey="id"
      stripedRows
      size="small"
      responsiveLayout="scroll"
      className="cb-table"
      footerColumnGroup={
        <ColumnGroup>
          <tr>
            <Column footer={totalLabel} />
            <Column footer={<strong>{euroFormatter.format(sumBalances(rows))}</strong>} footerClassName="cb-num" />
            <Column footer="" />
          </tr>
        </ColumnGroup>
      }
    >
      <Column field="account" header="Account" body={(row) => <span title={`${row.platform} account ${row.account_ref}`}>{row.account}</span>} />
      <Column
        field="balance"
        header="Cash Balance"
        alignHeader="right"
        headerClassName="cb-num"
        bodyClassName="cb-num"
        body={(row) => (row.balance !== null && row.balance !== undefined ? euroFormatter.format(row.balance) : '')}
      />
      <Column
        field="balance_date"
        header="Updated"
        headerClassName="cb-date"
        bodyClassName="cb-date"
        body={(row) => formatDate(row.balance_date)}
      />
    </DataTable>
  );

  // --- RENDER ONE WORKSPACE TAB ---
  const renderWorkspace = (workspace) => {
    const rows = data.rows.filter((row) => row.workspace === workspace.key);
    // Configured order first, then any other compartment that has a balance in this workspace.
    const compartments = [
      ...workspace.compartments,
      ...[...new Set(rows.map((row) => row.compartment))].filter((c) => !workspace.compartments.includes(c)),
    ].filter((compartment) => rows.some((row) => row.compartment === compartment));
    const grouped = compartments.length > 1;

    return (
      <div className="cb-tab">
        <div className="ex-stat-grid cb-stats">
          <div className="ex-stat">
            <span className="ex-stat-icon" aria-hidden="true"><i className="pi pi-wallet" /></span>
            <div className="ex-stat-body">
              <div className="ex-stat-label">Total cash balance</div>
              <div className="ex-stat-value">{euroFormatter.format(sumBalances(rows))}</div>
              <div className="ex-stat-hint">Sum of the latest balance per account</div>
            </div>
          </div>
          <div className="ex-stat">
            <span className="ex-stat-icon" aria-hidden="true"><i className="pi pi-building" /></span>
            <div className="ex-stat-body">
              <div className="ex-stat-label">Accounts</div>
              <div className="ex-stat-value">{rows.length}</div>
              <div className="ex-stat-hint">
                {grouped ? `Across ${compartments.length} compartments` : 'Included in this compartment'}
              </div>
            </div>
          </div>
          <div className="ex-stat">
            <span className="ex-stat-icon" aria-hidden="true"><i className="pi pi-calendar" /></span>
            <div className="ex-stat-body">
              <div className="ex-stat-label">Balance date</div>
              <div className="ex-stat-value">{filterDate ? formatDate(toQueryDate(filterDate)) : 'Latest'}</div>
              <div className="ex-stat-hint">{filterDate ? 'Selected in the date filter' : 'Most recent update per account'}</div>
            </div>
          </div>
        </div>

        <section className="ex-section cb-table-section">
          <div className="ex-section-header">
            <div>
              <h3 className="ex-section-title">Account balances</h3>
              <div className="ex-section-subtitle">
                {grouped ? 'Latest reconciled balance per account, by compartment' : 'Latest reconciled balance per account'}
              </div>
            </div>
            <Button
              label="Download Excel"
              icon="pi pi-download"
              className="p-button-outlined p-button-sm"
              onClick={() => downloadAllData(workspace)}
            />
          </div>
          {downloadMessage && <div className="ex-inline-error mb-2">{downloadMessage}</div>}

          {!rows.length && <div className="cb-empty">No cash balance on or before this date.</div>}
          {rows.length > 0 && !grouped && renderAccountsTable(rows, 'Total Cash Balance')}
          {grouped &&
            compartments.map((compartment) => (
              <div key={compartment} className="cb-compartment">
                <h4 className="cb-compartment-title">{compartment}</h4>
                {renderAccountsTable(rows.filter((row) => row.compartment === compartment), `${compartment} total`)}
              </div>
            ))}
        </section>
      </div>
    );
  };

  if (error) return <div className="content-container"><div className="ex-inline-error">{error}</div></div>;
  if (!data) return <div className="content-container"><LoadingState label="Loading cash balances" /></div>;

  return (
    <div className="content-container">
      <PageHeader
        title="Cash Balance"
        subtitle="Latest reconciled balance per account, by workspace."
        icon="pi-wallet"
        actions={
          <div className="cb-date-filter">
            <label htmlFor="cb-balance-date" className="cb-filter-label">Balance date</label>
            <Calendar
              inputId="cb-balance-date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.value)}
              dateFormat="dd/mm/yy"
              showIcon
              placeholder="Latest available"
            />
          </div>
        }
      />

      <TabView className="cash-balance-tabs" scrollable>
        {data.workspaces.map((workspace) => (
          <TabPanel key={workspace.key} header={workspace.label}>
            {renderWorkspace(workspace)}
          </TabPanel>
        ))}
      </TabView>
    </div>
  );
};

export default CashBalanceDashboard;
